import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { auth } from "../../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { httpsCallable, getFunctions } from "firebase/functions";
import { Icons } from "../../lib/Icons";
import jsPDF from "jspdf";

// Pre-made symbol collections for quick access
const SYMBOL_COLLECTIONS = [
  {
    id: 'greetings',
    name: 'Greetings',
    words: ['hello', 'goodbye', 'please', 'thank you', 'yes', 'no'],
    icon: 'ChatBubble',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    id: 'emotions',
    name: 'Emotions',
    words: ['happy', 'sad', 'angry', 'scared', 'tired', 'excited'],
    icon: 'Heart',
    color: 'from-pink-500 to-rose-500',
  },
  {
    id: 'actions',
    name: 'Actions',
    words: ['eat', 'drink', 'play', 'sleep', 'walk', 'stop'],
    icon: 'Hand',
    color: 'from-green-500 to-emerald-500',
  },
  {
    id: 'places',
    name: 'Places',
    words: ['home', 'school', 'toilet', 'playground', 'classroom', 'garden'],
    icon: 'Home',
    color: 'from-purple-500 to-violet-500',
  },
  {
    id: 'lunchtime',
    name: 'Lunchtime',
    words: ['lunch', 'food', 'water', 'apple', 'sandwich', 'finished'],
    icon: 'Clock',
    color: 'from-orange-500 to-amber-500',
  },
  {
    id: 'sensory',
    name: 'Sensory',
    words: ['loud', 'quiet', 'hot', 'cold', 'soft', 'hard'],
    icon: 'Sparkles',
    color: 'from-indigo-500 to-blue-500',
  },
];

// Example symbols for preview mode (pre-generated)
const EXAMPLE_SYMBOLS = [
  { word: 'hello', url: 'https://static.arasaac.org/pictograms/6499/6499_300.png', cached: true },
  { word: 'happy', url: 'https://static.arasaac.org/pictograms/9453/9453_300.png', cached: true },
  { word: 'eat', url: 'https://static.arasaac.org/pictograms/6458/6458_300.png', cached: true },
  { word: 'play', url: 'https://static.arasaac.org/pictograms/6511/6511_300.png', cached: true },
];

const SymbolGenerator = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inputText, setInputText] = useState("");
  const [symbols, setSymbols] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const printRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check for pre-populated words from navigation state (e.g., from timetable)
  useEffect(() => {
    if (location.state?.words && Array.isArray(location.state.words)) {
      const uniqueWords = [...new Set(location.state.words)].filter(Boolean);
      setInputText(uniqueWords.join(', '));
    }
  }, [location.state]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        // Show preview mode for unregistered users instead of redirecting
        setIsPreviewMode(true);
        setSymbols(EXAMPLE_SYMBOLS);
        setLoading(false);
        return;
      }

      setUser(currentUser);
      setIsPreviewMode(false);

      // Check subscription
      try {
        const functions = getFunctions(undefined, 'europe-west3');
        const checkSub = httpsCallable(functions, 'checkSoloSubscriptionFunc');
        const result = await checkSub();
        setHasSubscription(result.data.hasActiveSubscription);
      } catch (e) {
        console.error('Subscription check failed:', e);
        setHasSubscription(false);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  // Generate symbols for a single word
  const generateSymbol = async (word) => {
    const functions = getFunctions(undefined, 'europe-west3');
    const generateFunc = httpsCallable(functions, 'generateSymbolFunc');

    try {
      const result = await generateFunc({ word: word.trim() });
      return {
        word: result.data.word,
        url: result.data.publicUrl,
        base64: result.data.base64, // Store base64 for PDF generation
        cached: result.data.cached,
        error: null,
      };
    } catch (err) {
      console.error(`Error generating symbol for "${word}":`, err);
      return {
        word: word.trim(),
        url: null,
        base64: null,
        cached: false,
        error: err.message || 'Failed to generate',
      };
    }
  };

  // Generate symbols for multiple words
  const handleGenerate = async () => {
    if (!inputText.trim()) return;

    // Parse input: split by commas, newlines, or spaces
    const words = inputText
      .split(/[,\n]+/)
      .map(w => w.trim())
      .filter(w => w.length > 0 && w.length <= 50);

    if (words.length === 0) return;

    setGenerating(true);
    setError(null);

    try {
      // Generate symbols one by one (could optimize with batch endpoint)
      const results = [];
      for (const word of words) {
        const result = await generateSymbol(word);
        results.push(result);
        // Update UI progressively
        setSymbols(prev => [...prev, result]);
      }
    } catch (err) {
      setError(err.message || 'Failed to generate symbols');
    } finally {
      setGenerating(false);
    }
  };

  // Generate a pre-made collection
  const handleCollectionGenerate = async (collection) => {
    setSelectedCollection(collection.id);
    setSymbols([]); // Clear existing
    setGenerating(true);
    setError(null);

    try {
      for (const word of collection.words) {
        const result = await generateSymbol(word);
        setSymbols(prev => [...prev, result]);
      }
    } catch (err) {
      setError(err.message || 'Failed to generate collection');
    } finally {
      setGenerating(false);
    }
  };

  // Clear all symbols
  const handleClear = () => {
    setSymbols([]);
    setInputText("");
    setSelectedCollection(null);
  };

  // Print symbols
  const handlePrint = () => {
    window.print();
  };

  // Download a single symbol as PNG
  const handleDownload = async (symbol) => {
    if (!symbol.url) return;

    try {
      const response = await fetch(symbol.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `symbol-${symbol.word}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // Download all symbols as a single PDF
  const handleDownloadPDF = async () => {
    const validSymbols = symbols.filter(s => s.base64 || s.url);
    if (validSymbols.length === 0) return;

    setGenerating(true);

    try {
      // Create PDF (A4 size)
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Grid settings
      const margin = 10;
      const cols = 4;
      const cardWidth = (pageWidth - margin * 2 - (cols - 1) * 5) / cols;
      const cardHeight = cardWidth + 10; // Square image + label space
      const rows = Math.floor((pageHeight - margin * 2) / (cardHeight + 5));

      let currentCol = 0;
      let currentRow = 0;

      for (let i = 0; i < validSymbols.length; i++) {
        const symbol = validSymbols[i];

        // Calculate position
        const x = margin + currentCol * (cardWidth + 5);
        const y = margin + currentRow * (cardHeight + 5);

        // Draw card border
        pdf.setDrawColor(100);
        pdf.setLineWidth(0.5);
        pdf.rect(x, y, cardWidth, cardHeight);

        // Add image using stored base64 data
        if (symbol.base64) {
          try {
            const imgSize = cardWidth - 8;
            pdf.addImage(symbol.base64, 'PNG', x + 4, y + 4, imgSize, imgSize);
          } catch (err) {
            console.error(`Failed to add image for ${symbol.word}:`, err);
          }
        }

        // Add label
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0);
        pdf.text(
          symbol.word.charAt(0).toUpperCase() + symbol.word.slice(1),
          x + cardWidth / 2,
          y + cardHeight - 3,
          { align: 'center' }
        );

        // Move to next position
        currentCol++;
        if (currentCol >= cols) {
          currentCol = 0;
          currentRow++;
        }

        // New page if needed
        if (currentRow >= rows && i < validSymbols.length - 1) {
          pdf.addPage();
          currentCol = 0;
          currentRow = 0;
        }
      }

      // Add footer with attribution
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(128);
        pdf.text(
          'Symbols from ARASAAC (arasaac.org) - CC BY-NC-SA',
          pageWidth / 2,
          pageHeight - 5,
          { align: 'center' }
        );
      }

      // Save PDF with explicit blob download
      const filename = selectedCollection
        ? `symbols-${selectedCollection}.pdf`
        : `symbols-${new Date().toISOString().slice(0, 10)}.pdf`;

      // Create blob and download link explicitly
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.type = 'application/pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Print-specific styles */}
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body {
            background: white !important;
            margin: 0;
            padding: 0;
          }
          body * {
            visibility: hidden;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            padding: 15mm;
          }
          .print-area > div {
            display: grid !important;
            grid-template-columns: repeat(4, 1fr) !important;
            gap: 10mm !important;
          }
          .print-area .symbol-card {
            break-inside: avoid;
            page-break-inside: avoid;
            border: 2px solid #333 !important;
            border-radius: 8px !important;
            background: white !important;
            padding: 8mm !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
          }
          .print-area .symbol-card > div:first-child {
            width: 100% !important;
            aspect-ratio: 1 !important;
            border: 1px solid #ddd !important;
            border-radius: 6px !important;
            margin-bottom: 4mm !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: white !important;
          }
          .print-area img {
            max-width: 100% !important;
            max-height: 100% !important;
            object-fit: contain !important;
          }
          .print-area .symbol-label {
            color: black !important;
            font-size: 14pt !important;
            font-weight: bold !important;
            text-align: center !important;
            text-transform: capitalize !important;
          }
          .no-print {
            display: none !important;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
        }
      `}</style>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/solo/dashboard" className="flex items-center gap-3">
              <img
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Ed"
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-white">SEN-Ed</span>
            </Link>

            <div className="flex items-center gap-4">
              <Link
                to="/solo/dashboard"
                className="text-slate-400 hover:text-white transition-colors"
              >
                ← Back to Dashboard
              </Link>

              <div className="relative group">
                <button className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors">
                  {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-slate-800 rounded-xl shadow-xl border border-slate-700 py-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all">
                  <div className="px-4 py-2 border-b border-slate-700">
                    <p className="text-sm text-white font-medium truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden no-print">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 via-slate-900 to-purple-900/30"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-300 rounded-full text-sm mb-4">
              <Icons.Sparkles className="w-4 h-4" />
              AI-Powered Symbol Generator
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Create
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text"> AAC Symbols</span>
            </h1>
            <p className="text-lg text-slate-300">
              Type any word and generate beautiful, consistent symbols for communication boards, visual schedules, and more.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Input & Collections */}
          <div className="lg:col-span-1 space-y-6 no-print">
            {/* Word Input */}
            <div className="bg-slate-800 rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Icons.Pencil className="w-5 h-5 text-purple-400" />
                Enter Words
              </h2>

              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type words separated by commas or new lines...

Example:
hello, goodbye
eat, drink
happy, sad"
                className="w-full h-32 px-4 py-3 bg-slate-700 text-white placeholder-slate-400 rounded-xl border border-slate-600 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none resize-none"
              />

              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleGenerate}
                  disabled={generating || !inputText.trim()}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {generating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Icons.Sparkles className="w-4 h-4" />
                      Generate Symbols
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Quick Collections */}
            <div className="bg-slate-800 rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Icons.Folder className="w-5 h-5 text-purple-400" />
                Quick Collections
              </h2>
              <p className="text-sm text-slate-400 mb-4">
                Click a collection to generate all its symbols at once
              </p>

              <div className="space-y-2">
                {SYMBOL_COLLECTIONS.map((collection) => {
                  const IconComponent = Icons[collection.icon] || Icons.Folder;
                  return (
                    <button
                      key={collection.id}
                      onClick={() => handleCollectionGenerate(collection)}
                      disabled={generating}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${selectedCollection === collection.id
                          ? `bg-gradient-to-r ${collection.color} text-white`
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        } disabled:opacity-50`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedCollection === collection.id
                          ? 'bg-white/20'
                          : 'bg-slate-600'
                        }`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{collection.name}</p>
                        <p className="text-xs opacity-75">{collection.words.length} words</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column: Symbol Display */}
          <div className="lg:col-span-2">
            {/* Actions Bar */}
            {symbols.length > 0 && (
              <div className="flex items-center justify-between mb-6 no-print">
                <h2 className="text-lg font-semibold text-white">
                  Generated Symbols ({symbols.length})
                </h2>
                <div className="flex gap-3">
                  <button
                    onClick={handleDownloadPDF}
                    disabled={generating}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-colors disabled:opacity-50"
                  >
                    <Icons.Download className="w-4 h-4" />
                    Download PDF
                  </button>
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors"
                  >
                    <Icons.Printer className="w-4 h-4" />
                    Print
                  </button>
                  <button
                    onClick={handleClear}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-xl hover:bg-slate-600 hover:text-white transition-colors"
                  >
                    <Icons.XMark className="w-4 h-4" />
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 no-print">
                <p className="flex items-center gap-2">
                  <Icons.ExclamationTriangle className="w-5 h-5" />
                  {error}
                </p>
              </div>
            )}

            {/* Symbols Grid */}
            <div ref={printRef} className="print-area">
              {symbols.length === 0 ? (
                <div className="bg-slate-800 rounded-2xl p-12 text-center no-print">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-slate-700/50 flex items-center justify-center">
                    <Icons.Photo className="w-10 h-10 text-slate-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">No symbols yet</h3>
                  <p className="text-slate-400 mb-6">
                    Enter words above or select a collection to generate symbols
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {['hello', 'happy', 'eat', 'play'].map(word => (
                      <button
                        key={word}
                        onClick={() => {
                          setInputText(word);
                          handleGenerate();
                        }}
                        className="px-3 py-1.5 text-sm bg-slate-700 text-slate-300 rounded-full hover:bg-slate-600 transition-colors"
                      >
                        Try "{word}"
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {symbols.map((symbol, idx) => (
                    <div
                      key={`${symbol.word}-${idx}`}
                      className="symbol-card bg-slate-800 rounded-2xl p-4 flex flex-col items-center group"
                    >
                      {(symbol.base64 || symbol.url) ? (
                        <>
                          <div className="w-full aspect-square bg-white rounded-xl overflow-hidden mb-3 flex items-center justify-center">
                            <img
                              src={symbol.base64 || symbol.url}
                              alt={symbol.word}
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          <p className="symbol-label text-white font-medium text-center capitalize">
                            {symbol.word}
                          </p>
                          {symbol.cached && (
                            <span className="text-xs text-slate-500 mt-1 no-print">cached</span>
                          )}
                          <button
                            onClick={() => handleDownload(symbol)}
                            className="mt-2 text-xs text-purple-400 hover:text-purple-300 opacity-0 group-hover:opacity-100 transition-opacity no-print"
                          >
                            Download PNG
                          </button>
                        </>
                      ) : symbol.error ? (
                        <>
                          <div className="w-full aspect-square bg-slate-700 rounded-xl mb-3 flex items-center justify-center">
                            <Icons.ExclamationTriangle className="w-8 h-8 text-red-400" />
                          </div>
                          <p className="text-white font-medium text-center capitalize">
                            {symbol.word}
                          </p>
                          <p className="text-xs text-red-400 mt-1">{symbol.error}</p>
                        </>
                      ) : (
                        <>
                          <div className="w-full aspect-square bg-slate-700 rounded-xl mb-3 flex items-center justify-center">
                            <div className="w-8 h-8 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin"></div>
                          </div>
                          <p className="text-white font-medium text-center capitalize">
                            {symbol.word}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">Generating...</p>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Generating Indicator */}
            {generating && (
              <div className="mt-6 flex items-center justify-center gap-3 text-purple-400 no-print">
                <div className="w-5 h-5 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin"></div>
                <span>Generating symbols... This may take a moment</span>
              </div>
            )}
          </div>
        </div>

        {/* Tips Section */}
        <section className="mt-12 no-print">
          <h2 className="text-xl font-semibold text-white mb-6">Tips for Best Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Use Simple Words',
                description: 'Single words like "eat", "happy", "school" work best. Avoid complex phrases.',
                icon: 'CheckCircle',
              },
              {
                title: 'Be Specific',
                description: 'Use concrete nouns and actions. "apple" is better than "food".',
                icon: 'Target',
              },
              {
                title: 'Print at High Quality',
                description: 'For best print results, use a color printer and quality paper.',
                icon: 'Printer',
              },
            ].map((tip, idx) => {
              const TipIcon = Icons[tip.icon] || Icons.CheckCircle;
              return (
                <div key={idx} className="bg-slate-800/50 rounded-xl p-5">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
                    <TipIcon className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="text-white font-medium mb-1">{tip.title}</h3>
                  <p className="text-slate-400 text-sm">{tip.description}</p>
                </div>
              );
            })}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-800 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-slate-400">
              <img
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Ed"
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-medium text-white">SEN-Ed Resources</span>
            </div>
            <p className="text-sm text-slate-500">
              Pictograms from{' '}
              <a
                href="https://arasaac.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300"
              >
                ARASAAC
              </a>
              {' '}(Aragonese Centre for AAC) - CC BY-NC-SA License
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SymbolGenerator;

